"use client";

import React from "react";
import { Star, Sparkles, WandSparkles, Palette, FlaskConical, Gem, Briefcase, HeartHandshake, CalendarDays, Coffee, Flower2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const experiences = [{
  title: "Paint & Create Parties",
  description: "Unleash your inner artist with our guided painting sessions. Perfect for all ages, we bring the canvases, paints, and creative magic directly to you.",
  icon: Palette,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/magical_painting_party_creative_session.png",
  link: "#paint-parties"
}, {
  title: "Slime & Potions Lab",
  description: "A gooey, glittery, and science-filled adventure! Kids love creating their own custom slime & magical concoctions in our safe and supervised lab experience.",
  icon: FlaskConical,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/kids_science_lab_magical_experiment.png",
  link: "#slime-lab"
}, {
  title: "Jewelry Design Parties",
  description: "Design and create your own stunning beaded masterpieces. From elegant bracelets to whimsical necklaces, we provide all the beads and tools for a sparkling creative session.",
  icon: Gem,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/jewelry_making_party.png",
  link: "#private-events"
}, {
  title: "Murder Mystery Masquerades",
  description: "Step into a world of intrigue and elegance. Our masquerade mysteries combine theatrical storytelling with sophisticated puzzles for an unforgettable night of secrets.",
  icon: Briefcase,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/masquerade_mystery_party.png",
  link: "#corporate"
}, {
  title: "Enchanting\nGame Nights",
  description: "Transform your gathering into an evening of high-stakes elegance. Our game nights combine classic entertainment with premium styling for a sophisticated and magical social experience.",
  icon: HeartHandshake,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/fancy_game_night_social_event.png",
  link: "#fundraisers"
}, {
  title: "Holiday Celebrations",
  description: "Seasonal magic brought to life! Whether it's a winter wonderland or a spooky soirée, we handle the details so you can enjoy the festivities.",
  icon: CalendarDays,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/fancy_easter_party_celebration.png",
  link: "#holiday-events"
}, {
  title: "Tea Parties",
  description: "Step into a world of elegance with our whimsical tea services. Fine china, delicate treats, and a touch of vintage magic for your special gathering.",
  icon: Coffee,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/enchanted_magical_tea_party_celebration.png",
  link: "#tea-parties"
}, {
  title: "Spa Parties",
  description: "The ultimate relaxation spa party in a home. Pamper your guests with soothing treatments and a serene atmosphere designed for pure bliss.",
  icon: Sparkles,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/in_home_spa_party_experience.png",
  link: "#spa-parties"
}, {
  title: "Yoga Experiences",
  description: "Connect mind, body, and spirit in an enchanting setting. Our guided sessions bring a sense of peace and magical flow to any group or event.",
  icon: Flower2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562315/img/yoga_experience_wellness_event.png",
  link: "#yoga-experiences"
}];
export default function SignatureMagicalExperiences() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll();
  return <section id="signaturemagicalexperiences" className="relative w-full py-24 overflow-hidden bg-transparent">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[10%] left-[5%] text-secondary/30 animate-pulse duration-[3000ms]">
          <Sparkles className="w-12 h-12" />
        </div>
        <div className="absolute bottom-[15%] right-[10%] text-secondary/30 animate-pulse duration-[4000ms]">
          <Star className="w-8 h-8" />
        </div>
        <div className="absolute top-[40%] right-[5%] text-secondary/10 rotate-12">
          <WandSparkles className="w-64 h-64" />
        </div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        
        {}
        <div ref={headerRef} className={cn("text-center mb-16 transition-all duration-1000 ease-out transform", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-[1px] w-12 bg-linear-to-r from-transparent to-secondary"></div>
            <Star className="w-5 h-5 text-secondary fill-secondary animate-spin-slow" />
            <div className="h-[1px] w-12 bg-linear-to-l from-transparent to-secondary"></div>
          </div>
          
          <h2 className="text-5xl md:text-6xl font-normal text-primary mb-6 drop-shadow-sm">
            Signature Magical Experiences
          </h2>
          
          <p className="max-w-4xl mx-auto font-serif leading-relaxed text-2xl !text-[hsl(0,0%,100%)] !italic">Discover our curated collection of enchanting events designed to spark joy and wonder.</p>

          <div className="flex justify-center mt-8">
             <div className="flex gap-3 text-secondary/60">
                <Sparkles className="w-5 h-5" />
                <Sparkles className="w-5 h-5" />
                <Sparkles className="w-5 h-5" />
             </div>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {experiences.map((experience, index) => <div key={index} data-index={index} className={cn("group transition-all duration-700 ease-out transform", isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
          transitionDelay: `${index * 100}ms`
        }}>
              <Card className="h-full border border-secondary/40 bg-white/20 backdrop-blur-md shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 overflow-hidden flex flex-col">
                <div className="relative">
                  <AspectRatio ratio={16 / 9} className="bg-muted">
                    <img src={experience.image} alt={experience.title} className="object-cover w-full h-full transition-transform duration-1000 group-hover:scale-110" />
                  </AspectRatio>
                  <div className="absolute inset-0 bg-linear-to-t from-primary/40 to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-300" />
                  <div className="absolute bottom-4 right-4 bg-white/90 p-3 rounded-full shadow-lg text-secondary border border-secondary/20">
                    <experience.icon className="w-6 h-6" />
                  </div>
                </div>

                <CardHeader className="pb-2">
                  <CardTitle className="text-3xl text-primary font-normal tracking-wide !text-center">
                    {experience.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-grow">
                  <CardDescription className="text-lg text-primary font-serif leading-relaxed opacity-90">
                    {experience.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center justify-center p-1 rounded-full border border-secondary/40 bg-white/40 backdrop-blur-sm">
            <span className="px-8 py-3 text-primary font-serif italic text-xl">Custom Experiences Available Upon Request</span>
          </div>
        </div>

      </div>
    </section>;
}